import { Ref, ref } from '@vue/composition-api';

export interface UseRollingIndex {
	next(): void;
	prev(): void;
	selectedIdx: Ref<null | number>;
}

/**
 * Allows tracking the index of a selected item in a list, rolling around to `null`
 * when attempting to page past the last or first item. Paging from `null` will
 * then move to either the first or last item in the list
 *
 * @param items The items to track
 * @returns UseRollingIndex
 */
export function useRollingIndex<T>(items: Ref<T[]>): UseRollingIndex {
	const selectedIdx = ref(-1 as null | number);
	const nextIndex = (currentIdx: null | number, inc: number, numResults: number, nullFallback: number) => {
		if (numResults === 0) {
			return null;
		}
		if (currentIdx === null) {
			return nullFallback;
		}
		const next = currentIdx + inc;
		return (next >= numResults || next < 0) ? null : next;
	};
	const next = () => {
		selectedIdx.value = nextIndex(selectedIdx.value, 1, items.value.length, 0);
	};
	const prev = () => {
		selectedIdx.value = nextIndex(selectedIdx.value, -1, items.value.length, items.value.length - 1);
	};
	return {
		next,
		prev,
		selectedIdx,
	};
}
